import React from 'react';
import { Box, SxProps, Theme } from '@mui/material';
import { Telegram as TelegramIcon } from '@mui/icons-material';
import { landing, BOT_URL } from './tokens';

const merge = (base: SxProps<Theme>, sx?: SxProps<Theme>): SxProps<Theme> =>
  [base, ...(Array.isArray(sx) ? sx : [sx])] as SxProps<Theme>;

interface WithSx {
  sx?: SxProps<Theme>;
  children?: React.ReactNode;
}

export const Container: React.FC<WithSx> = ({ sx, children }) => (
  <Box
    sx={merge(
      { width: '100%', maxWidth: landing.maxWidth, mx: 'auto', px: 3 },
      sx
    )}
  >
    {children}
  </Box>
);

export const Section: React.FC<WithSx & { id?: string; label?: string }> = ({
  id,
  label,
  sx,
  children,
}) => (
  <Box
    component='section'
    id={id}
    aria-label={label}
    sx={merge({ py: { xs: 9, md: 13 }, scrollMarginTop: '72px' }, sx)}
  >
    {children}
  </Box>
);

export const Eyebrow: React.FC<WithSx> = ({ sx, children }) => (
  <Box
    component='span'
    sx={merge(
      {
        fontSize: 13,
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: landing.accent,
      },
      sx
    )}
  >
    {children}
  </Box>
);

export const SectionTitle: React.FC<WithSx> = ({ sx, children }) => (
  <Box
    component='h2'
    sx={merge(
      {
        fontSize: 'clamp(34px, 4.2vw, 54px)',
        lineHeight: 1.04,
        letterSpacing: '-0.03em',
        fontWeight: 700,
        textWrap: 'balance',
      },
      sx
    )}
  >
    {children}
  </Box>
);

export const Lead: React.FC<WithSx> = ({ sx, children }) => (
  <Box
    component='p'
    sx={merge(
      { fontSize: 18, lineHeight: 1.6, color: landing.ink2, maxWidth: 640 },
      sx
    )}
  >
    {children}
  </Box>
);

export const SectionHeader: React.FC<{
  eyebrow?: string;
  title: string;
  lead?: string;
}> = ({ eyebrow, title, lead }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75, maxWidth: 760 }}>
    {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
    <SectionTitle>{title}</SectionTitle>
    {lead && <Lead>{lead}</Lead>}
  </Box>
);

export const Card: React.FC<WithSx & { component?: React.ElementType }> = ({
  sx,
  children,
  component = 'div',
}) => (
  <Box
    component={component}
    sx={merge(
      {
        bgcolor: landing.surface,
        border: `1px solid ${landing.line}`,
        borderRadius: '24px',
        p: { xs: 3, md: 3.5 },
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      },
      sx
    )}
  >
    {children}
  </Box>
);

export const IconTile: React.FC<WithSx & { size?: number }> = ({
  size = 44,
  sx,
  children,
}) => (
  <Box
    component='span'
    sx={merge(
      {
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: '14px',
        bgcolor: landing.accentSoft,
        color: landing.accent,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '& svg': { fontSize: Math.round(size * 0.5) },
      },
      sx
    )}
  >
    {children}
  </Box>
);

const ctaTones = {
  accent: { bgcolor: landing.accent, color: '#FFFFFF', hover: '#0B6159' },
  ink: { bgcolor: landing.ink, color: landing.bg, hover: '#22343A' },
  white: { bgcolor: '#FFFFFF', color: landing.ink, hover: '#E9F3F2' },
};

/** The page's one action: open the Fin in Telegram. */
export const TelegramCta: React.FC<
  WithSx & {
    label: string;
    tone?: keyof typeof ctaTones;
    size?: 'sm' | 'md' | 'lg';
    icon?: boolean;
  }
> = ({ label, tone = 'accent', size = 'lg', icon = true, sx }) => {
  const colors = ctaTones[tone];
  const height = { sm: 44, md: 52, lg: 54 }[size];
  return (
    <Box
      component='a'
      href={BOT_URL}
      target='_blank'
      rel='noopener noreferrer'
      sx={merge(
        {
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1.25,
          minHeight: height,
          px: size === 'sm' ? 2.25 : 3.25,
          borderRadius: '999px',
          bgcolor: colors.bgcolor,
          color: colors.color,
          fontWeight: 600,
          fontSize: size === 'sm' ? 15 : size === 'md' ? 16 : 17,
          textDecoration: 'none',
          whiteSpace: 'nowrap',
          transition: 'background-color 150ms ease',
          '&:hover': { bgcolor: colors.hover },
          '& svg': { fontSize: size === 'sm' ? 18 : 20 },
        },
        sx
      )}
    >
      {icon && <TelegramIcon />}
      {label}
    </Box>
  );
};

export const Chip: React.FC<WithSx> = ({ sx, children }) => (
  <Box
    component='span'
    sx={merge(
      {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        px: 1.5,
        py: 0.75,
        borderRadius: '999px',
        bgcolor: landing.surface,
        border: `1px solid ${landing.line}`,
        fontSize: 13,
        fontWeight: 600,
        color: landing.ink2,
      },
      sx
    )}
  >
    {children}
  </Box>
);

/** Small circle with a person's initial, for group avatars. */
export const Initial: React.FC<{
  letter: string;
  color: string;
  tint: string;
  size?: number;
  overlap?: boolean;
}> = ({ letter, color, tint, size = 30, overlap = false }) => (
  <Box
    component='span'
    aria-hidden='true'
    sx={{
      width: size,
      height: size,
      ml: overlap ? '-9px' : 0,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: letter.length > 1 ? 11 : 12,
      fontWeight: 700,
      bgcolor: tint,
      color,
      border: `2px solid ${landing.surface}`,
    }}
  >
    {letter}
  </Box>
);
